<script setup>
import { computed, getCurrentInstance, toRef } from 'vue';
import { useDialogA11y } from 'dashboard/composables/useDialogA11y';

const props = defineProps({
  show: { type: Boolean, default: false },
  title: { type: String, default: '' },
  side: {
    type: String,
    default: 'bottom',
    validator: value => ['right', 'left', 'bottom'].includes(value),
  },
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md', 'lg', 'xl', '2xl'].includes(value),
  },
  closeLabel: { type: String, default: 'Fechar' },
  closeIcon: {
    type: String,
    default: 'x',
    validator: value => ['x', 'arrow-left'].includes(value),
  },
  fullScreen: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const { uid } = getCurrentInstance();
const overlayStyle = { backgroundColor: 'rgba(15, 23, 42, 0.42)' };
const titleId = `lais-drawer-${uid}`;
const labelledBy = computed(() => (props.title ? titleId : undefined));
const transitionName = computed(() => `lais-drawer-${props.side}`);
const sizeClass = computed(
  () =>
    ({
      sm: 'sm:max-w-sm',
      md: 'sm:max-w-lg',
      lg: 'sm:max-w-2xl',
      xl: 'sm:w-[min(calc(100vw-2rem),46rem)] md:w-[min(calc(100vw-3rem),58rem)] lg:w-[min(calc(100vw-4rem),74rem)] xl:w-[min(94vw,90rem)] 2xl:w-[min(92vw,104rem)]',
      '2xl':
        'sm:w-[min(calc(100vw-2rem),52rem)] md:w-[min(calc(100vw-3rem),68rem)] lg:w-[min(calc(100vw-4rem),82rem)] xl:w-[min(96vw,108rem)] 2xl:w-[min(94vw,120rem)]',
    })[props.size]
);
const panelClass = computed(() => {
  const base =
    'absolute flex min-h-0 flex-col border-lais-border bg-lais-bg-card shadow-lais-pop outline-none';

  if (props.side === 'bottom') {
    return [
      base,
      props.fullScreen
        ? 'inset-0 h-[100dvh] max-h-none rounded-none border'
        : 'inset-x-0 bottom-0 max-h-[calc(100dvh-0.75rem)] rounded-t-lais-sheet border border-b-0',
    ];
  }

  const isExpandedPanel = props.size === 'xl' || props.size === '2xl';
  const expandedPositionClass =
    props.side === 'right'
      ? 'inset-x-0 h-[100dvh] w-full sm:inset-y-3 sm:right-3 sm:left-auto sm:h-[calc(100dvh-1.5rem)]'
      : 'inset-x-0 h-[100dvh] w-full sm:inset-y-3 sm:left-3 sm:right-auto sm:h-[calc(100dvh-1.5rem)]';

  return [
    base,
    props.side === 'right'
      ? 'inset-y-0 right-0 border-l'
      : 'inset-y-0 left-0 border-r',
    isExpandedPanel ? expandedPositionClass : 'h-[100dvh] w-full sm:h-full',
    sizeClass.value,
  ];
});
const bodyClass = computed(() =>
  props.size === 'xl' || props.size === '2xl'
    ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6 2xl:px-10 2xl:py-8'
    : 'min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4'
);
const headerClass = computed(() =>
  props.size === 'xl' || props.size === '2xl'
    ? 'flex shrink-0 items-center justify-between gap-3 border-b border-lais-border-soft px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] sm:px-6 sm:py-4 lg:px-8 2xl:px-10'
    : 'flex shrink-0 items-center justify-between gap-3 border-b border-lais-border-soft px-5 pb-4 pt-[calc(1rem+env(safe-area-inset-top))]'
);
const footerClass = computed(() =>
  props.size === 'xl' || props.size === '2xl'
    ? 'min-w-0 max-h-[44dvh] shrink-0 overflow-y-auto overscroll-contain border-t border-lais-border-soft bg-lais-bg-card px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:px-6 sm:py-4 lg:px-8 2xl:px-10 2xl:py-5'
    : 'max-h-[40dvh] shrink-0 overflow-y-auto overscroll-contain border-t border-lais-border-soft px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:max-h-none sm:overflow-visible'
);
const { dialogRef } = useDialogA11y({
  isOpen: toRef(props, 'show'),
  onClose: () => emit('close'),
});
</script>

<template>
  <div>
    <div
      v-if="show"
      class="fixed inset-0 z-lais-drawer"
      :style="overlayStyle"
      @click.self="emit('close')"
    >
      <Transition :name="transitionName" appear>
        <div
          ref="dialogRef"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="labelledBy"
          tabindex="-1"
          :class="panelClass"
        >
          <div
            v-if="side === 'bottom'"
            class="mx-auto mt-3 h-1.5 w-14 shrink-0 rounded-full bg-lais-border"
          />
          <header :class="headerClass">
            <div class="min-w-0 flex-1">
              <slot name="header">
                <h2
                  v-if="title"
                  :id="titleId"
                  class="break-words font-redHatDisplay text-lais-heading-md font-semibold text-lais-text"
                >
                  {{ title }}
                </h2>
              </slot>
            </div>
            <button
              type="button"
              class="grid size-9 shrink-0 place-items-center rounded-full text-lais-text-muted transition hover:bg-lais-primary-50 hover:text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
              :aria-label="closeLabel"
              @click="emit('close')"
            >
              <span
                v-if="closeIcon === 'arrow-left'"
                class="i-lucide-arrow-left size-4"
                aria-hidden="true"
              />
              <span v-else class="i-lucide-x size-4" aria-hidden="true" />
            </button>
          </header>
          <div :class="bodyClass">
            <slot name="body">
              <slot />
            </slot>
          </div>
          <footer v-if="$slots.footer" :class="footerClass">
            <slot name="footer" />
          </footer>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped>
.lais-drawer-bottom-enter-active,
.lais-drawer-bottom-leave-active,
.lais-drawer-left-enter-active,
.lais-drawer-left-leave-active,
.lais-drawer-right-enter-active,
.lais-drawer-right-leave-active {
  transition:
    opacity 160ms ease,
    transform 180ms ease;
}

.lais-drawer-bottom-enter-from,
.lais-drawer-bottom-leave-to {
  opacity: 0;
  transform: translateY(24px);
}

.lais-drawer-left-enter-from,
.lais-drawer-left-leave-to {
  opacity: 0;
  transform: translateX(-24px);
}

.lais-drawer-right-enter-from,
.lais-drawer-right-leave-to {
  opacity: 0;
  transform: translateX(24px);
}

@media (prefers-reduced-motion: reduce) {
  .lais-drawer-bottom-enter-active,
  .lais-drawer-bottom-leave-active,
  .lais-drawer-left-enter-active,
  .lais-drawer-left-leave-active,
  .lais-drawer-right-enter-active,
  .lais-drawer-right-leave-active {
    transition: opacity 0.001ms;
    transform: none;
  }
}
</style>
